// BookDetails.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from '../../../global-components/src';
import { useMessage } from "../../../global-components/src/index";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BookPlaceholder } from "../placeholder";
import "./style.css"
import { READBOOKS_API_URL, READBOOKS_URL } from "../../../global-components/src/utils/vars";
import { censorText, clearText, errorMessage } from "../../../global-components/src/utils/text";
import Modal from "../../../global-components/src/components/modal/modal";
import { Helmet } from "react-helmet-async";
import { BookOpenText } from "lucide-react";
import NotFound from "../../errors/NotFound";
import { isMyLove } from "../../../global-components/src/utils/auth";
import { getBookImage } from "../../../global-components/src/utils/book";
import { Book } from "../../../global-components/src/types";
import { formatDate } from "../../../utils/utils";
import { CatalogBook } from "../../../types";

const formatCurrency = (value: number, currency = "BRL") =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value);


export default function BookDetails() {
    const [showDelete, setShowDelete] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [book, setBook] = useState<Book | null>(null);
    const [catalogBook, setCatalogBook]                 = useState<CatalogBook | null>(null);
    const [canEdit, setCanEdit] = useState(false);
    const [errorStatusCode, setErrorStatusCode] = useState<404 | null>(null);
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [processedText, setProcessedText] = useState<string>("");
    const shareUrl = `${READBOOKS_URL}/livros/${book?.uuid}`;
    const [canRead, setCanRead] = useState(false);
    const [hasPurchased, setHasPurchased] = useState(false);

    const loadData = async () => {
        // Carrega data locais ANTES de buscar na API
        const stored = localStorage.getItem(`book-${uuid}`);
        if (stored) {
            const dataLocal = JSON.parse(stored);
            setCanEdit(dataLocal?.id_usuario === user?.id);
            setBook(dataLocal)
            setProcessedText(localStorage.getItem(`processedText-${uuid}`) ?? "")
        }
        
        let url = `${READBOOKS_API_URL}/books/${uuid}`;

        if (loading) return;
            setLoading(true);

        try {
            const response = await axios.get(url, {
                headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
                },
            });

            const data = response.data;
            
            setCatalogBook(response.data.catalog_book);
            setCanRead(response.data.can_read);
            setHasPurchased(response.data.has_purchased);

            if (data?.book){
                setCanEdit(data?.book.id_usuario === user?.id);
                const cleanDescription = clearText(censorText(data?.book.descricao_livro), false);
                setProcessedText(cleanDescription)
                localStorage.setItem(`book-${uuid}`, JSON.stringify(data.book))
                localStorage.setItem(`processedText-${uuid}`, cleanDescription)
                setBook(data?.book);
            }

        } catch (err: any) {
            setErrorStatusCode(err.response?.status || 500);
            if(err.response?.status !== 404) {
                setLoading(false);
                const messageError = errorMessage(err);
                setMessage(messageError, "error");
                console.error("Erro ao pesquisar: ", messageError);
                console.error("Erro ao pesquisar: ", err);
            }
        } finally {
            setLoading(false);
        }
    };

    const ReadButton = () => {
        // 1. Pode ler (grátis ou já comprou)
        if (canRead) {
            return (
                <>
                    <button onClick={() => navigate(`/livros/ler/${catalogBook?.uuid}`)} className="btn-livro btn btn-success" style={{ width: "100%", marginTop: "10px" }}>
                        Ler agora
                    </button>
                </>
            );
        }   

        // 2. Livro pago — ainda não comprou
        if (catalogBook && catalogBook.price > 0 && !hasPurchased) {
            return (
                <button
                    onClick={() => navigate(`/catalogo/${catalogBook.id}/comprar`)}
                    className="btn-livro btn btn-warning"
                    style={{ width: "100%", marginTop: "10px" }}
                >
                    Comprar por {formatCurrency(catalogBook.price, catalogBook.currency)}
                </button>
            );
        }
        // 3. PDF ainda não disponível (edge case: grátis mas sem arquivo)
        return null;
    };

    const deleteBook = async () => {
        if (loadingDelete) return;
            setLoadingDelete(true);

        try {
            const response = await axios.delete(`${READBOOKS_API_URL}/books/${uuid}`, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            if (user?.id) {
                localStorage.removeItem(`ai_reading_analysis_${user.id}`);
            }
            localStorage.removeItem("ai_reading_analysis");
            setShowDelete(false);
            setMessage(response.data.message, "success");
            navigate("/");
        } catch (err: any) {
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error("Erro ao pesquisar: ", messageError);
            console.error("Erro ao pesquisar: ", err);
        } finally {
            setLoadingDelete(false);
        }
    };

    useEffect(() => {
        if(uuid) loadData();
    }, [uuid]);

    const percent = book?.total_paginas ? (book?.paginas_lidas / book?.total_paginas) * 100 : 0;

    // const formatDate = (date?: string) =>date ? new Date(date).toLocaleDateString("pt-BR") : null;

    const copyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    if (errorStatusCode === 404) {
        return (
            <NotFound text="Livro não encontrado" showLink={false}>
                <BookOpenText className="icon-empty"/>
            </NotFound>
        );
    }

    return (
        <div className="margin-top-menu container container-livro">
            <Modal
                onClose={() => setShowDelete(false)}
                textBtnCancelar="Cancelar" 
                title={`Deseja apagar o livro${isMyLove(user) ? ", meu amor" : ""}`} 
                text={`Tem certeza de que deseja apagar esse livro? Não é possivel recuperar os dados${isMyLove(user) ? ", meu lovezinho" : ""}.`}
                show={showDelete}
                handleConfirm={deleteBook}
                isLoading={loadingDelete}
            />

            <Modal
                textBtnConfirmar="Ok"
                idModal="modal-share"
                onOpen={copyLink}
                coverMobile={false}
                onClose={() => setShowShare(false)}
                title={`Link copiado${isMyLove(user) ? ", meu amor" : ""}!`} 
                text={`O link do livro foi copiado${isMyLove(user) ? ", meu lovezinho" : ""}: ${READBOOKS_URL}/livros/${book?.uuid}`}
                show={showShare}
                handleConfirm={() => {setShowShare(false);}}
                isLoading={loadingDelete}
            />

            <div className="row">
                <div className="col-4">
                    {loading && !book ? (
                        <BookPlaceholder className="img-livro-selecionado"/>
                    ) :(
                        <>
                        <div className={`div-img-livro-selecionado ${
                        book?.img_livro === "../img/book_transparent.png"
                            ? "img-capa-default"
                            : ""
                        }`}
                        style={
                        book?.img_livro === "../img/book_transparent.png"
                            ? { backgroundImage: `url(${book?.img_livro})` }
                            : {}
                        }
                    >
                        {book?.img_livro !== "../img/book_transparent.png" ? (
                        <img
                            onError={(e) => { e.currentTarget.src = "/img/book_transparent.png"; }}
                            src={getBookImage(book?.img_livro, true)}
                            alt="Capa do livro"
                            className="img-livro-selecionado"
                        />
                        ) : (
                        <h1>{book?.nome_livro}</h1>
                        )}
                    </div>
                        </>
                    )}
                </div>

                {!loading || book ? (
                    <div className="col-8 col-info-livro">
                        <h1 className="nome-livro-lido">{book?.nome_livro}</h1>

                        <div className="row row-livro-lido">
                            <div className="col">
                                <h4>Tempo de leitura: {book?.tempo_lido}</h4>
                                <h4>
                                    Páginas Lidas: {book?.paginas_lidas}
                                    {book?.total_paginas && ` / ${book?.total_paginas}`}
                                    {percent > 0 &&
                                    percent < 100 &&
                                    ` - ${percent.toFixed(1).replace(".", ",")}%`}
                                </h4>
                                {book?.data_inicio && (
                                    <h4>Data de início: {formatDate(book?.data_inicio)}</h4>
                                )}
                                {book?.data_termino && (
                                    <h4>Data de término: {formatDate(book?.data_termino)}</h4>
                                )}

                                <h4 className={percent === 100 ? "status-success" : "status-danger"} style={{ borderRadius: 5, padding: "1px 5px" }}>
                                    Terminou: {percent == 100 ? "Sim" : "Não"}
                                </h4>
                            </div>

                            {canEdit && (
                                <div className="col col-livro-lido">
                                    <div className="col-12 d-flex justify-content-between">
                                        <a href={`/livros/editar/${book?.uuid}`} className="btn-livro btn btn-outline-primary">
                                            Editar
                                        </a>
                                        <button className="btn-livro btn btn-outline-danger" onClick={() => setShowDelete(true)} >
                                            Excluir
                                        </button>
                                    </div>

                                    
                                    <ReadButton />

                                    <button className="btn-livro btn btn-primary mt-2" onClick={() => setShowShare(true)} style={{ width: "100%" }}>
                                    Compartilhar
                                    </button>
                                </div>
                            )}

                            <p className="text-post book-description post text-expand" dangerouslySetInnerHTML={{ __html: processedText }}/>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
